import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation } from "react-router";

import { Button } from "@/design-system/Button";
import { acceptInvitation, acceptPasswordReset } from "@/shared/api";
import { clearViewAs } from "@/shared/session/viewAs";

/** The server's rule, said before the press rather than after it. */
const MIN_LENGTH = 8;

/** The same form sets a first password from an invitation and a new one from
 *  a reset link the administrator issued (2026-10-14). */
const MODES = {
  invite: {
    title: "Принять приглашение",
    missing: "В ссылке нет приглашения.",
    done: "Доступ создан. Теперь войди под приглашённым логином.",
    refused: "Приглашение недействительно или уже использовано.",
    submit: "Создать доступ",
    send: (token: string, password: string) => acceptInvitation({ body: { token, password } }),
  },
  reset: {
    title: "Новый пароль",
    missing: "В ссылке нет кода сброса.",
    done: "Пароль изменён. Все прежние входы закрыты — войди с новым паролем.",
    refused: "Ссылка недействительна, уже использована или истекла. Попроси новую у администратора.",
    submit: "Сохранить пароль",
    send: (token: string, password: string) => acceptPasswordReset({ body: { token, password } }),
  },
} as const;

export function AcceptInvitationPage({ mode = "invite" }: { readonly mode?: keyof typeof MODES }): React.JSX.Element {
  const text = MODES[mode];
  const location = useLocation();
  const token = new URLSearchParams(location.hash.slice(1)).get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      // Writes are refused while a view is on; accepting is this tab's own act.
      clearViewAs();
      const { error, response } = await text.send(token, password);
      // A short password is not a dead link: saying so kept the first client
      // out (2026-10-13). The invitation stays valid until one is accepted.
      if (response?.status === 422) throw new Error("password");
      if (error !== undefined || (response?.status ?? 0) >= 400) {
        throw new Error("invitation refused");
      }
    },
  });

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background-base p-6">
      <section className="w-full max-w-sm rounded-lg border border-border-subtle bg-surface-default p-6">
        <h1 className="text-lg font-semibold text-text-primary">{text.title}</h1>
        {token === "" ? (
          <p role="alert" className="mt-4 text-sm text-status-negative">
            {text.missing}
          </p>
        ) : mutation.isSuccess ? (
          <div className="mt-4 flex flex-col gap-3">
            <p className="text-body-main text-text-primary">
              {text.done}
            </p>
            <Link className="text-action-primary underline" to="/app">
              Перейти ко входу
            </Link>
          </div>
        ) : (
          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (password.length >= MIN_LENGTH && password === confirmation && !mutation.isPending) {
                mutation.mutate();
              }
            }}
          >
            <label className="text-sm text-text-secondary">
              Новый пароль
              <input
                minLength={MIN_LENGTH}
                aria-describedby="password-rule"
                className="mt-1 block w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => { setPassword(event.target.value); }}
              />
            </label>
            <p id="password-rule" className={`-mt-2 text-helper-sm ${password !== "" && password.length < MIN_LENGTH ? "text-status-warning" : "text-text-muted"}`}>
              Не короче {MIN_LENGTH} символов{password !== "" && password.length < MIN_LENGTH ? ` — сейчас ${String(password.length)}` : ""}. Буквы, цифры и знаки — любые.
            </p>
            <label className="text-sm text-text-secondary">
              Повтори пароль
              <input
                className="mt-1 block w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                type="password"
                autoComplete="new-password"
                required
                value={confirmation}
                onChange={(event) => { setConfirmation(event.target.value); }}
              />
            </label>
            {confirmation !== "" && password !== confirmation && (
              <p role="alert" className="text-sm text-status-negative">
                Пароли не совпадают.
              </p>
            )}
            {mutation.isError && (
              <p role="alert" className="text-sm text-status-negative">
                {mutation.error.message === "password"
                  ? `Пароль слишком короткий — нужно не меньше ${String(MIN_LENGTH)} символов. Ссылка по-прежнему действует.`
                  : text.refused}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={
                mutation.isPending || password.length < MIN_LENGTH || password !== confirmation
              }
            >
              {text.submit}
            </Button>
          </form>
        )}
      </section>
    </main>
  );
}
